import { z } from "zod";

export const citationSchema = z.object({
  title: z.string().min(1),
  url: z.string().url(),
  type: z.enum(["manufacturer", "retailer", "review", "video"])
});

export const specItemSchema = z.object({
  key: z.string().min(1),
  value: z.string().min(1),
  unit: z.string().optional(),
  sources: z.array(z.string().url()).default([])
});

export const mediaSchema = z.object({
  images: z.array(z.string().url()).default([]),
  videos: z
    .array(
      z.object({
        title: z.string().min(1),
        youtubeId: z.string().min(1)
      })
    )
    .default([])
});

export const canonicalProductSchema = z.object({
  summary: z.string().max(600),
  specs: z.array(specItemSchema),
  pros: z.array(z.string()).max(6).default([]),
  cons: z.array(z.string()).max(6).default([]),
  best_for: z.array(z.string()).max(5).default([]),
  gotchas: z.array(z.string()).max(5).default([]),
  faq: z
    .array(
      z.object({
        q: z.string().min(1),
        a: z.string().min(1)
      })
    )
    .default([]),
  media: mediaSchema,
  citations: z.array(citationSchema)
});

export const productInsertSchema = z.object({
  name: z.string().min(2),
  brand: z.string().min(1),
  model: z.string().min(1),
  category: z.string().min(1)
});

export const productRecordSchema = productInsertSchema.extend({
  id: z.string(),
  slug: z.string(),
  status: z.enum(["pending", "ready", "error"]),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
});

export const reviewInsertSchema = z.object({
  product_id: z.string().min(1),
  rating: z.number().int().min(1).max(5),
  title: z.string().min(3),
  body: z.string().min(20),
  turnstileToken: z.string().min(1)
});

export const reviewRecordSchema = z.object({
  id: z.number(),
  product_id: z.string(),
  user_id: z.string(),
  rating: z.number(),
  title: z.string(),
  body: z.string(),
  status: z.string(),
  created_at: z.string(),
  user_name: z.string().optional(),
  user_avatar_url: z.string().optional()
});

export const reviewAggregateSchema = z.object({
  average: z.number(),
  count: z.number()
});

export const sessionUserSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().email(),
  avatarUrl: z.string().url().nullable(),
  isAdmin: z.boolean().optional().default(false)
});

export type CanonicalProduct = z.infer<typeof canonicalProductSchema>;
export type ProductRecord = z.infer<typeof productRecordSchema>;
export type ProductInsert = z.infer<typeof productInsertSchema>;
export type ReviewInsert = z.infer<typeof reviewInsertSchema>;
export type ReviewRecord = z.infer<typeof reviewRecordSchema>;
export type ReviewAggregate = z.infer<typeof reviewAggregateSchema>;
export type SessionUser = z.infer<typeof sessionUserSchema>;
